/**
 * The **Analytics & Insights** panel — spec §F5.2, as **client specification S3** rebuilt it.
 *
 * S3's layout is three columns: the theme rail (~5%), this panel (~20%) and the map (~75%).
 * What the panel holds is *navigation, then insights*:
 *
 *     theme (the rail)  →  the theme's datasets  →  the selected dataset's insights
 *
 * ## The two tabs are gone
 *
 * §F5.2 gave the panel an Indicators tab and a Layers tab, and client round 4 (E2) made
 * them a mode — switching to one cleared the other's mapping. S3 removes the pair outright:
 * **indicators leave the explorer's body** and live behind the rail's own Indicators icon
 * (`DomainRail`), which guests cannot use, and everything outside that icon is a
 * **dataset**. So there is nothing left for a tab to switch between: the rail already says
 * what the panel is about, and a second control saying it again is the thing R3 and E2 were
 * both trying to fix.
 *
 * The E2 clearance survives where it still means something. It has moved to
 * `applyThemeDefault`: picking a theme with a dataset default drops the mapped variable and
 * picking one with a variable default drops the raster, so the map still shows one thing at
 * a time. What has gone is the *toast* — E2's message explained a tab switch, and there is
 * no tab switch to explain; the rail chip lights up and the panel repaints, which is the
 * message. `paneSwitch.ts` and `switchSidebarTab` are kept for the Indicators icon, which
 * is still a mode change in exactly E2's sense.
 *
 * ## Under the Indicators icon
 *
 * `IndicatorsPane` and `SidebarAnalytics`, unchanged — the code paths the later dashboard
 * task needs are **moved, not deleted**. The pane may say "indicators" there, because there
 * it is what the reader asked for; everywhere else the panel says "datasets" (S3 clause 5).
 *
 * A guest can never reach that state through the rail (the chip is disabled), and the panel
 * refuses it a second time here rather than trusting the control: `themeAllowed` is the §I2
 * mirror of §I1, and a mirror that only one surface checks is a mirror with a hole in it.
 *
 * Collapse is unchanged — the mockup's `#sidebar.collapsed { margin-left: -332px }` driven
 * from the §F1 store, with the whole dock hidden rather than just the panel (R6), so the
 * rail's chips leave the tab order with the panel they filter.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { openAccess } from '../../lib/access';
import { fetchConfig, queryKeys } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { InsightsPanel } from '../widgets/InsightsPanel';
import { SidebarAnalytics } from '../widgets/SidebarAnalytics';
import { DomainRail } from './DomainRail';
import { IndicatorsPane } from './IndicatorsPane';
import { LayersPane } from './LayersPane';
import { INDICATORS_THEME, themeAllowed, themeEntry, themeOf } from './themes';

export function Sidebar() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.sidebarOpen);
  const role = useAppStore((s) => s.role);
  const domFilter = useAppStore((s) => s.domFilter);
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });

  const theme = themeOf(domFilter);
  const entry = themeEntry(config, theme);
  const onIndicators =
    theme === INDICATORS_THEME && themeAllowed(entry, role, openAccess(config));

  // §F5.2: the body class is the hook every off-sidebar widget offsets against.
  useEffect(() => {
    document.body.classList.toggle('sb-open', open);
    return () => document.body.classList.remove('sb-open');
  }, [open]);

  return (
    <div id="sbDock" className={open ? undefined : 'collapsed'} aria-hidden={!open}>
      <DomainRail />
      <aside
        id="sidebar"
        className={open ? undefined : 'collapsed'}
        aria-hidden={!open}
        aria-label={t('sidebar.panelTitle')}
      >
        {/* S3's own name for this column. It replaces the two tabs in the same 34 px, and
            it is the one place the panel says what it is — the sections below say what
            they are about. */}
        <div id="sbPanelHead">
          <Icon name="chart" size={15} />
          <span id="sbPanelTitle">{t('sidebar.panelTitle')}</span>
        </div>

        {onIndicators ? <IndicatorsPane /> : <LayersPane />}

        {/* The insights half. Under the Indicators icon that is the R7 analytics section,
            whose subject is the mapped indicator; for a theme it is S3's five blocks over
            the selected dataset (`/analysis/insights`). One slot, two subjects, because the
            question is the same one: *what is the number behind what I am looking at?* */}
        {onIndicators ? <SidebarAnalytics /> : <InsightsPanel />}
      </aside>
    </div>
  );
}

/**
 * The collapse button — mockup `#collapseSb`, a `.mapbtn` pinned top-left **inside**
 * `#mapwrap` (so it stays put when the pane slides out). §F5.10's search box takes the
 * slot to its right in Task 7.
 */
export function SidebarToggle() {
  const { t } = useTranslation();
  const open = useAppStore((s) => s.sidebarOpen);
  const setOpen = useAppStore((s) => s.setSidebarOpen);
  return (
    <button
      type="button"
      className="mapbtn"
      id="collapseSb"
      aria-expanded={open}
      aria-controls="sidebar"
      title={t(open ? 'sidebar.collapse' : 'sidebar.expand')}
      aria-label={t(open ? 'sidebar.collapse' : 'sidebar.expand')}
      onClick={() => setOpen(!open)}
    >
      <Icon name="layers" />
    </button>
  );
}

export default Sidebar;
